import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useMgmtOptions, datePreset } from "../hooks/useMgmtOptions";
import PageHeader from "../components/layout/PageHeader";
import { formatINR, formatCompactINR, formatNum } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Target, TrendingUp, TrendingDown, AlertTriangle, Users, Wallet, Stethoscope, Activity, IndianRupee,
} from "lucide-react";

const DATE_PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"],
  ["last_week", "Last Week"], ["this_month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"],
];

function fmt(v, money) { return money ? formatCompactINR(v) : formatNum(v); }

function KpiCard({ card }) {
  const ach = card.achievement || 0;
  const good = ach >= 100;
  const color = good ? "#10B981" : ach >= 80 ? "#F59E0B" : "#EF4444";
  return (
    <div data-testid={`mgmt-kpi-${card.name}`} className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
      <p className="text-xs tracking-[0.1em] uppercase text-slate-400">{card.name}</p>
      <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
        <span className="text-slate-500">Goal</span>
        <span className="text-right tabular text-slate-300">{fmt(card.goal, card.money)}</span>
        <span className="text-slate-500">Actual</span>
        <span className="text-right tabular font-display text-lg font-bold text-white">{fmt(card.actual, card.money)}</span>
        <span className="text-slate-500">Gap</span>
        <span className="text-right tabular" style={{ color: card.gap <= 0 ? "#10B981" : "#EF4444" }}>{fmt(Math.abs(card.gap), card.money)}{card.gap > 0 ? " short" : ""}</span>
      </div>
      <div className="mt-3">
        <div className="h-2 rounded-full bg-[#0F172A] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(ach, 100)}%`, background: color }} />
        </div>
        <p className="text-right text-xs mt-1 font-medium" style={{ color }}>{ach}% achieved</p>
      </div>
    </div>
  );
}

function CostCard({ cpl, roas }) {
  const cplColor = cpl.status === "Above Target" ? "#EF4444" : cpl.status === "Below Target" ? "#10B981" : "#F59E0B";
  const roasGood = roas.gap >= 0;
  return (
    <>
      <div data-testid="mgmt-kpi-CPL" className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
        <p className="text-xs tracking-[0.1em] uppercase text-slate-400">CPL</p>
        <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
          <span className="text-slate-500">Goal CPL</span><span className="text-right tabular text-slate-300">{formatINR(cpl.goal)}</span>
          <span className="text-slate-500">Actual CPL</span><span className="text-right tabular font-display text-lg font-bold text-white">{formatINR(cpl.actual)}</span>
          <span className="text-slate-500">Variance</span><span className="text-right tabular" style={{ color: cplColor }}>{formatINR(Math.abs(cpl.variance))}</span>
        </div>
        <p className="mt-3 text-xs font-medium inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ color: cplColor, background: `${cplColor}22` }}>
          {cpl.status === "Above Target" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {cpl.status}
        </p>
      </div>
      <div data-testid="mgmt-kpi-ROAS" className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
        <p className="text-xs tracking-[0.1em] uppercase text-slate-400">ROAS</p>
        <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
          <span className="text-slate-500">Goal ROAS</span><span className="text-right tabular text-slate-300">{roas.goal}x</span>
          <span className="text-slate-500">Actual ROAS</span><span className="text-right tabular font-display text-lg font-bold" style={{ color: roasGood ? "#10B981" : "#EF4444" }}>{roas.actual}x</span>
          <span className="text-slate-500">Gap</span><span className="text-right tabular" style={{ color: roasGood ? "#10B981" : "#EF4444" }}>{roas.gap > 0 ? "+" : ""}{roas.gap}x</span>
        </div>
      </div>
    </>
  );
}

const FUNNEL_STAGES = [
  ["Ad Spend", "ad_spend", Wallet, true], ["Leads", "leads", Users, false],
  ["Qualified", "qualified_leads", Target, false], ["Consultations", "consultations", Stethoscope, false],
  ["Sales", "sales", Activity, false], ["Revenue", "revenue", IndianRupee, true],
];

export default function ManagementDashboard() {
  const opts = useMgmtOptions();
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(datePreset("this_month"));
  const [f, setF] = useState({ branch_id: "all", vertical: "all", campaign: "all", employee_id: "all", service: "all", platform: "all" });
  const [data, setData] = useState(null);
  const [cmpMode, setCmpMode] = useState("weekly");
  const [cmp, setCmp] = useState(null);
  const [perfGroup, setPerfGroup] = useState("campaign");
  const [perf, setPerf] = useState([]);
  const [actions, setActions] = useState([]);

  const params = { ...f, start: range.start, end: range.end };

  const load = useCallback(async () => {
    const [s, p, a] = await Promise.all([
      api.get("/mgmt/summary", { params }),
      api.get("/mgmt/performance", { params: { ...params, group: perfGroup } }),
      api.get("/mgmt/actions", { params: { start: range.start, end: range.end, branch_id: f.branch_id, vertical: f.vertical } }),
    ]);
    setData(s.data); setPerf(p.data); setActions(a.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), perfGroup]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/mgmt/comparison", { params: { mode: cmpMode, ...f } }).then((r) => setCmp(r.data)).catch(() => {});
  }, [cmpMode, JSON.stringify(f)]);

  const setPresetVal = (p) => { setPreset(p); if (p !== "custom") setRange(datePreset(p)); };

  const sel = (label, key, items, valueKey = "name", labelKey = "name") => (
    <Select value={f[key]} onValueChange={(v) => setF({ ...f, [key]: v })}>
      <SelectTrigger data-testid={`mgmt-filter-${key}`} className="h-9 w-full bg-[#1E293B] border-white/10 text-slate-200 rounded-lg text-xs"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-72">
        <SelectItem value="all">{label}: All</SelectItem>
        {(items || []).map((it) => <SelectItem key={it.id || it} value={it[valueKey] || it}>{it[labelKey] || it}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const maxFunnel = data ? Math.max(data.funnel.leads, 1) : 1;

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Management Dashboard" subtitle="Goal vs Actual across every dimension — auto-computed from the Daily Funnel" />

      {/* Filters */}
      <div className="rounded-xl bg-[#0B1120] border border-white/10 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
          <Select value={preset} onValueChange={setPresetVal}>
            <SelectTrigger data-testid="mgmt-date-preset" className="h-9 bg-[#1E293B] border-white/10 text-slate-200 rounded-lg text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              {DATE_PRESETS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {sel("Branch", "branch_id", opts.branches, "id")}
          {sel("Vertical", "vertical", opts.verticals)}
          {sel("Campaign", "campaign", opts.campaigns)}
          {sel("Employee", "employee_id", opts.employees, "id")}
          {sel("Service", "service", opts.services)}
          {sel("Platform", "platform", opts.platforms)}
          {preset === "custom" && (
            <div className="flex gap-1 col-span-2 md:col-span-1">
              <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 bg-[#1E293B] border-white/10 text-white text-xs" />
              <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 bg-[#1E293B] border-white/10 text-white text-xs" />
            </div>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Budget remaining strip */}
          <div className="rounded-xl bg-[#1E293B] border border-white/10 p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
            <span className="text-slate-400">Budget Used <span className="text-white font-semibold ml-1">{formatCompactINR(data.funnel.ad_spend)}</span></span>
            <span className="text-slate-400">Budget Remaining <span className="font-semibold ml-1" style={{ color: data.budget_remaining >= 0 ? "#10B981" : "#EF4444" }}>{formatCompactINR(data.budget_remaining)}</span></span>
            <span className="text-slate-400">Revenue/Lead <span className="text-white font-semibold ml-1">{formatINR(data.derived.revenue_per_lead)}</span></span>
            <span className="text-slate-400">Lead→Sale <span className="text-white font-semibold ml-1">{data.derived.lead_to_sale_pct}%</span></span>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {data.cards.map((c) => <KpiCard key={c.name} card={c} />)}
            <CostCard cpl={data.cpl} roas={data.roas} />
          </div>

          {/* Main funnel */}
          <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
            <h2 className="font-display text-lg font-semibold mb-4">Main Funnel</h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {FUNNEL_STAGES.map(([label, key, Icon, money], i) => {
                const val = data.funnel[key];
                const pct = money ? 100 : Math.min((val / maxFunnel) * 100, 100);
                return (
                  <div key={key} className="rounded-lg bg-[#0F172A] border border-white/10 p-4">
                    <Icon className="w-4 h-4 text-[#38BDF8]" />
                    <p className="text-xs text-slate-400 mt-2">{label}</p>
                    <p className="font-display text-lg font-bold tabular mt-1">{money ? formatCompactINR(val) : formatNum(val)}</p>
                    {!money && <div className="h-1.5 rounded-full bg-[#1E293B] mt-2 overflow-hidden"><div className="h-full bg-[#38BDF8]" style={{ width: `${pct}%` }} /></div>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Weekly/Monthly comparison */}
      <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Comparison</h2>
          <div className="flex gap-1 bg-[#0F172A] rounded-full p-1 border border-white/10">
            {[["weekly", "This vs Last Week"], ["monthly", "This vs Last Month"]].map(([k, l]) => (
              <button key={k} data-testid={`cmp-${k}`} onClick={() => setCmpMode(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${cmpMode === k ? "bg-[#38BDF8] text-[#0F172A]" : "text-slate-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2 px-3">Metric</th><th className="text-right py-2 px-3">{cmpMode === "weekly" ? "This Week" : "This Month"}</th>
              <th className="text-right py-2 px-3">{cmpMode === "weekly" ? "Last Week" : "Last Month"}</th><th className="text-right py-2 px-3">Change</th><th className="text-right py-2 px-3">Growth</th></tr></thead>
            <tbody>
              {(cmp?.rows || []).map((row) => (
                <tr key={row.metric} className="border-b border-white/5">
                  <td className="py-2.5 px-3 text-slate-200">{row.metric}{row.is_cost && <span className="text-[10px] text-slate-500 ml-1">(cost)</span>}</td>
                  <td className="py-2.5 px-3 text-right tabular text-slate-200">{row.money ? formatCompactINR(row.current) : (row.metric === "ROAS" ? row.current + "x" : formatNum(row.current))}</td>
                  <td className="py-2.5 px-3 text-right tabular text-slate-400">{row.money ? formatCompactINR(row.previous) : (row.metric === "ROAS" ? row.previous + "x" : formatNum(row.previous))}</td>
                  <td className="py-2.5 px-3 text-right tabular text-slate-300">{row.change >= 0 ? "+" : ""}{row.money ? formatCompactINR(row.change) : formatNum(row.change)}</td>
                  <td className="py-2.5 px-3 text-right tabular font-medium" style={{ color: row.is_cost ? (row.growth > 0 ? "#EF4444" : "#10B981") : (row.growth >= 0 ? "#10B981" : "#EF4444") }}>
                    {row.growth > 0 ? "+" : ""}{row.growth}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance breakdown */}
      <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Performance</h2>
          <div className="flex gap-1 bg-[#0F172A] rounded-full p-1 border border-white/10">
            {[["campaign", "Campaign"], ["branch", "Branch"], ["employee", "Employee"]].map(([k, l]) => (
              <button key={k} data-testid={`perf-${k}`} onClick={() => setPerfGroup(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${perfGroup === k ? "bg-[#38BDF8] text-[#0F172A]" : "text-slate-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2 px-3 capitalize">{perfGroup}</th><th className="text-right py-2 px-3">Spend</th><th className="text-right py-2 px-3">Leads</th>
              <th className="text-right py-2 px-3">Qualified</th><th className="text-right py-2 px-3">Consults</th><th className="text-right py-2 px-3">Sales</th>
              <th className="text-right py-2 px-3">Revenue</th><th className="text-right py-2 px-3">CPL</th><th className="text-right py-2 px-3">ROAS</th></tr></thead>
            <tbody>
              {perf.map((row, i) => (
                <tr key={i} className={`border-b border-white/5 ${i % 2 ? "bg-[#0F172A]/40" : ""}`}>
                  <td className="py-2.5 px-3 text-slate-200 font-medium">{row.name}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatCompactINR(row.ad_spend)}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatNum(row.leads)}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatNum(row.qualified_leads)}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatNum(row.consultations)}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatNum(row.sales)}</td>
                  <td className="py-2.5 px-3 text-right tabular text-[#10B981]">{formatCompactINR(row.revenue)}</td>
                  <td className="py-2.5 px-3 text-right tabular">{formatINR(row.cpl)}</td>
                  <td className="py-2.5 px-3 text-right tabular font-medium text-[#38BDF8]">{row.roas}x</td>
                </tr>
              ))}
              {perf.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-500">No data available for the selected filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Required */}
      <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-[#F59E0B]" /><h2 className="font-display text-lg font-semibold">Action Required</h2></div>
        {actions.length === 0 ? (
          <p className="text-sm text-slate-500">Everything is on track for the selected filters. 🎯</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {actions.map((a, i) => {
              const c = a.severity === "high" ? "#EF4444" : a.severity === "medium" ? "#F59E0B" : "#38BDF8";
              return (
                <div key={i} data-testid="mgmt-action-item" className="rounded-lg bg-[#0F172A] border border-white/10 p-4" style={{ borderLeft: `3px solid ${c}` }}>
                  <p className="text-sm font-medium text-white flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{a.title}</p>
                  <p className="text-xs text-slate-400 mt-1">{a.detail}</p>
                  <p className="text-xs text-slate-500 mt-2"><span className="uppercase tracking-wider">Review · </span>{a.review}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
